import React, { useCallback, useState } from 'react';

import {
  Atm,
  AtmOperationType,
  AtmOption,
  AtmTheme,
  AtmTransaction,
  AtmTransactionStatus,
} from '../../types/atm';
import { trackAtmTransaction } from '../../utils/trackingUtils';
import AtmResultModal from './AtmResultModal';

interface AtmMachineProps {
  atm: Atm;
  onTransaction: (transaction: AtmTransaction) => void;
  className?: string;
  disabled?: boolean;
}

interface AtmState {
  isActive: boolean;
  currentScreen: 'welcome' | 'pin' | 'select' | 'processing' | 'result' | 'receipt';
  enteredPin: string;
  selectedOperation?: AtmOperationType;
  currentTransaction?: AtmTransaction;
  isProcessing: boolean;
  showReceipt: boolean;
  showCelebrationModal: boolean;
}

const AtmMachine: React.FC<AtmMachineProps> = ({
  atm,
  onTransaction,
  className = '',
  disabled = false,
}) => {
  const [state, setState] = useState<AtmState>({
    isActive: false,
    currentScreen: 'welcome',
    enteredPin: '',
    isProcessing: false,
    showReceipt: false,
    showCelebrationModal: false,
  });

  // Theme-based styling
  const getThemeStyles = useCallback((theme: AtmTheme) => {
    switch (theme) {
      case AtmTheme.CLASSIC:
        return {
          machine: 'bg-gray-800 border-gray-600',
          screen: 'bg-green-900 text-green-400',
          button: 'bg-gray-700 hover:bg-gray-600 text-white',
          accent: 'text-green-400',
        };
      case AtmTheme.MODERN:
        return {
          machine: 'bg-slate-900 border-purple-500',
          screen: 'bg-purple-900 text-purple-200',
          button: 'bg-purple-700 hover:bg-purple-600 text-white',
          accent: 'text-purple-400',
        };
      case AtmTheme.RETRO:
        return {
          machine: 'bg-amber-100 border-amber-800',
          screen: 'bg-amber-900 text-amber-200',
          button: 'bg-amber-700 hover:bg-amber-600 text-white',
          accent: 'text-amber-400',
        };
      case AtmTheme.DARK:
        return {
          machine: 'bg-black border-gray-400',
          screen: 'bg-gray-900 text-gray-300',
          button: 'bg-gray-800 hover:bg-gray-700 text-white',
          accent: 'text-blue-400',
        };
      default:
        return {
          machine: 'bg-blue-900 border-blue-600',
          screen: 'bg-blue-800 text-blue-100',
          button: 'bg-blue-700 hover:bg-blue-600 text-white',
          accent: 'text-blue-300',
        };
    }
  }, []);

  const themeStyles = getThemeStyles(atm.theme);

  // Handle PIN entry
  const handlePinDigit = (digit: string) => {
    if (state.enteredPin.length < 4) {
      setState(prev => ({
        ...prev,
        enteredPin: prev.enteredPin + digit,
      }));
    }
  };

  const clearPin = () => {
    setState(prev => ({ ...prev, enteredPin: '' }));
  };

  const submitPin = () => {
    if (atm.atmSettings.requirePin && state.enteredPin !== (atm.atmSettings.defaultPin || '0000')) {
      // Invalid PIN
      setState(prev => ({
        ...prev,
        enteredPin: '',
        currentScreen: 'pin',
      }));
      return;
    }

    setState(prev => ({
      ...prev,
      currentScreen: 'select',
    }));
  };

  // Handle cancel - reset to initial screen (PIN or select)
  const handleCancel = () => {
    setState(prev => ({
      ...prev,
      enteredPin: '',
      selectedOperation: undefined,
      currentTransaction: undefined,
      isProcessing: false,
      showReceipt: false,
      currentScreen: atm.atmSettings.requirePin ? 'pin' : 'select',
    }));
  };

  // Handle operation selection
  const selectOperation = (operationId: string, operationType: AtmOperationType) => {
    setState(prev => ({
      ...prev,
      selectedOperation: operationType,
      currentScreen: 'processing',
      isProcessing: true,
    }));

    // Process transaction after delay
    setTimeout(() => {
      processTransaction(operationId, operationType);
    }, 2000);
  };

  // Process transaction
  const processTransaction = (operationId: string, operationType: AtmOperationType) => {
    let operation = atm.options.find(op => op.id === operationId);

    // Handle default operations (Balance, Cancel) that aren't in atm.options
    if (!operation) {
      if (operationType === AtmOperationType.BALANCE) {
        // Generate a realistic balance for demo purposes
        const balanceAmount = Math.floor(Math.random() * 10000) + 500; // $500-$10,500
        operation = {
          id: 'balance',
          text: 'Check Balance',
          type: AtmOperationType.BALANCE,
          amount: balanceAmount,
          enabled: true,
        };
      } else {
        return; // Unknown operation
      }
    }

    if (!operation.enabled) {
      return;
    }

    // Create transaction
    const transaction: AtmTransaction = {
      id: `txn_${Date.now()}`,
      atmId: atm.id,
      optionId: operation.id || 'default',
      optionText: operation.text,
      type: operationType,
      amount: operation.amount,
      timestamp: new Date(),
      userId: 'anonymous', // Will be updated by context
      status: AtmTransactionStatus.COMPLETED,
    };

    // Update state with result - show celebration modal instead of result screen
    setState(prev => ({
      ...prev,
      currentTransaction: transaction,
      showCelebrationModal: true,
      isProcessing: false,
    }));

    // Track analytics
    trackAtmTransaction(atm.id, operationType, operation.amount);

    // Notify parent
    onTransaction(transaction);
  };

  // Show receipt
  const showReceipt = () => {
    setState(prev => ({
      ...prev,
      currentScreen: 'receipt',
      showReceipt: true,
    }));
  };

  // Reset ATM
  const resetAtm = () => {
    setState({
      isActive: false,
      currentScreen: 'welcome',
      enteredPin: '',
      isProcessing: false,
      showReceipt: false,
      showCelebrationModal: false,
    });
  };

  // Close celebration modal
  const closeCelebrationModal = () => {
    setState(prev => ({
      ...prev,
      showCelebrationModal: false,
      currentScreen: 'select', // Return to selection screen
    }));
  };

  // Handle view receipt from modal
  const handleViewReceiptFromModal = () => {
    setState(prev => ({
      ...prev,
      showCelebrationModal: false,
      currentScreen: 'receipt',
      showReceipt: true,
    }));
  };

  // Handle transact again from modal
  const handleTransactAgain = () => {
    setState(prev => ({
      ...prev,
      showCelebrationModal: false,
      currentScreen: 'select',
      currentTransaction: undefined,
    }));
  };

  // Start ATM interaction
  const startAtm = () => {
    setState(prev => ({
      ...prev,
      isActive: true,
      currentScreen: atm.atmSettings.requirePin ? 'pin' : 'select',
    }));
  };

  // Render keypad
  const renderKeypad = () => (
    <div className="grid grid-cols-3 gap-2 mt-4">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, '', 0, ''].map((key, index) => (
        <button
          key={index}
          onClick={() => key && handlePinDigit(key.toString())}
          className={`${
            key ? themeStyles.button : 'invisible'
          } p-3 rounded font-mono text-lg transition-colors`}
          disabled={disabled || !key}
        >
          {key}
        </button>
      ))}
    </div>
  );

  // Render operation buttons
  const renderOperations = () => {
    // Get complementary balance button style for each theme
    const getBalanceButtonStyle = () => {
      switch (atm.theme) {
        case AtmTheme.CLASSIC:
          return 'bg-blue-700 hover:bg-blue-600 text-white';
        case AtmTheme.MODERN:
          return 'bg-teal-600 hover:bg-teal-500 text-white';
        case AtmTheme.RETRO:
          return 'bg-orange-600 hover:bg-orange-500 text-white';
        case AtmTheme.DARK:
          return 'bg-indigo-600 hover:bg-indigo-500 text-white';
        case AtmTheme.DEFAULT:
        default:
          return 'bg-green-600 hover:bg-green-500 text-white';
      }
    };

    return (
      <div className="space-y-3 mt-4">
        {/* User-defined operations */}
        {atm.options.map((operation: AtmOption) => (
          <button
            key={operation.id}
            onClick={() => selectOperation(operation.id || 'default', operation.type)}
            disabled={disabled || !operation.enabled}
            className={`w-full p-4 rounded-lg transition-colors text-left ${
              operation.enabled
                ? themeStyles.button
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            <div className="flex justify-between items-center">
              <span className="font-medium">{operation.type}</span>
              <span className={themeStyles.accent}>${operation.amount}</span>
            </div>
            <div className="text-sm opacity-75 mt-1">
              {operation.enabled ? 'Available' : 'Disabled'}
            </div>
          </button>
        ))}

        {/* Default Balance button */}
        <button
          onClick={() => selectOperation('balance', AtmOperationType.BALANCE)}
          disabled={disabled}
          className={`w-full p-4 rounded-lg transition-colors text-left ${getBalanceButtonStyle()}`}
        >
          <div className="flex justify-between items-center">
            <span className="font-medium">Check Balance</span>
            <span className="text-current opacity-75">Free</span>
          </div>
          <div className="text-sm opacity-75 mt-1">View your current balance</div>
        </button>

        {/* Default Cancel button */}
        <button
          onClick={handleCancel}
          disabled={disabled}
          className={`w-full p-4 rounded-lg transition-colors text-left bg-red-600 hover:bg-red-500 text-white`}
        >
          <div className="flex justify-between items-center">
            <span className="font-medium">Cancel</span>
            <span className="text-red-200">Exit</span>
          </div>
          <div className="text-sm opacity-75 mt-1">Return to main screen</div>
        </button>
      </div>
    );
  };

  // Render current screen content
  const renderScreen = () => {
    switch (state.currentScreen) {
      case 'welcome':
        return (
          <div className="text-center">
            <h3 className={`text-xl font-bold ${themeStyles.accent} mb-4`}>
              Welcome to {atm.name}
            </h3>
            <p className="text-sm mb-6">{atm.description || 'Select an option to begin'}</p>
            <button
              onClick={startAtm}
              disabled={disabled}
              className={`${themeStyles.button} px-6 py-3 rounded-lg font-medium transition-colors`}
            >
              Start Transaction
            </button>
          </div>
        );

      case 'pin':
        return (
          <div className="text-center">
            <h3 className={`text-lg font-bold ${themeStyles.accent} mb-4`}>Enter PIN</h3>
            <div className="flex justify-center mb-4">
              <div className="flex space-x-2">
                {[0, 1, 2, 3].map(i => (
                  <div
                    key={i}
                    className={`w-4 h-4 rounded-full border-2 ${
                      i < state.enteredPin.length
                        ? `${themeStyles.accent} bg-current`
                        : 'border-current'
                    }`}
                  />
                ))}
              </div>
            </div>
            {renderKeypad()}
            <div className="flex space-x-2 mt-6">
              <button
                onClick={clearPin}
                className={`${themeStyles.button} px-4 py-2 rounded flex-1 text-sm`}
              >
                Clear
              </button>
              <button
                onClick={submitPin}
                className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded flex-1 text-sm"
              >
                Enter
              </button>
              <button
                onClick={handleCancel}
                className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded flex-1 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        );

      case 'select':
        return (
          <div>
            <h3 className={`text-lg font-bold ${themeStyles.accent} mb-4 text-center`}>
              Select Transaction
            </h3>
            {renderOperations()}
          </div>
        );

      case 'processing':
        return (
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-current border-t-transparent rounded-full mx-auto mb-4"></div>
            <h3 className={`text-lg font-bold ${themeStyles.accent} mb-2`}>
              Processing Transaction...
            </h3>
            <p className="text-sm">Please wait</p>
          </div>
        );

      case 'result':
        return (
          <div className="text-center">
            <div className={`text-2xl ${themeStyles.accent} mb-4`}>✓</div>
            <h3 className={`text-lg font-bold ${themeStyles.accent} mb-4`}>
              {state.currentTransaction?.type === AtmOperationType.BALANCE
                ? 'Balance Inquiry'
                : 'Transaction Complete'}
            </h3>
            {state.currentTransaction && (
              <div className="space-y-2 text-sm">
                {state.currentTransaction.type === AtmOperationType.BALANCE ? (
                  <>
                    <p>Your Current Balance:</p>
                    <p className={`text-2xl font-bold ${themeStyles.accent}`}>
                      ${state.currentTransaction.amount?.toLocaleString()}
                    </p>
                    <p className="text-xs opacity-75">
                      as of {(state.currentTransaction.timestamp as Date).toLocaleTimeString()}
                    </p>
                  </>
                ) : (
                  <>
                    <p>Operation: {state.currentTransaction.type}</p>
                    <p>Amount: ${state.currentTransaction.amount}</p>
                    <p>Time: {(state.currentTransaction.timestamp as Date).toLocaleTimeString()}</p>
                  </>
                )}
              </div>
            )}
            <div className="flex space-x-2 mt-6">
              <button
                onClick={showReceipt}
                className={`${themeStyles.button} px-4 py-2 rounded flex-1 text-sm`}
              >
                Receipt
              </button>
              <button
                onClick={resetAtm}
                className={`${themeStyles.button} px-4 py-2 rounded flex-1 text-sm`}
              >
                New Transaction
              </button>
              <button
                onClick={handleCancel}
                className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded flex-1 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        );

      case 'receipt':
        return (
          <div className="text-center">
            <h3 className={`text-lg font-bold ${themeStyles.accent} mb-4`}>Transaction Receipt</h3>
            {state.currentTransaction && (
              <div className="text-left text-xs font-mono space-y-1 bg-white text-black p-4 rounded mx-auto max-w-64">
                <div className="text-center border-b pb-2 mb-2">
                  <div className="font-bold">{atm.name}</div>
                  <div>{new Date().toLocaleString()}</div>
                </div>
                <div>ID: {state.currentTransaction.id}</div>
                <div>Operation: {state.currentTransaction.type}</div>
                <div>Amount: ${state.currentTransaction.amount}</div>
                <div>Time: {state.currentTransaction.timestamp.toLocaleString()}</div>
                <div className="border-t pt-2 mt-2 text-center">Thank you for using {atm.name}</div>
              </div>
            )}
            <div className="flex space-x-2 mt-4">
              <button
                onClick={resetAtm}
                className={`${themeStyles.button} px-6 py-2 rounded flex-1 text-sm`}
              >
                Done
              </button>
              <button
                onClick={handleCancel}
                className="bg-red-600 hover:bg-red-500 text-white px-6 py-2 rounded flex-1 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className={`${className} max-w-md mx-auto`}>
        {/* ATM Machine */}
        <div className={`${themeStyles.machine} border-4 rounded-lg p-6 shadow-2xl`}>
          {/* Screen */}
          <div className={`${themeStyles.screen} rounded p-6 mb-6 min-h-80`}>{renderScreen()}</div>

          {/* ATM Info */}
          <div className="text-center text-white">
            <div className="text-sm opacity-75">ATM ID: {atm.id.slice(-8)}</div>
            {atm.isPublic && <div className="text-xs opacity-50 mt-1">Public ATM</div>}
          </div>
        </div>

        {/* Status Indicators */}
        <div className="mt-4 text-center text-sm text-gray-600">
          <div className="flex justify-center items-center space-x-4">
            <div className="flex items-center">
              <div
                className={`w-2 h-2 rounded-full mr-2 ${disabled ? 'bg-red-500' : 'bg-green-500'}`}
              ></div>
              {disabled ? 'Offline' : 'Online'}
            </div>
            <div className="flex items-center">
              <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
              {atm.options.filter(op => op.enabled).length} options available
            </div>
          </div>
        </div>
      </div>

      {/* Celebration Modal */}
      <AtmResultModal
        isOpen={state.showCelebrationModal}
        onClose={closeCelebrationModal}
        transaction={state.currentTransaction || null}
        onViewReceipt={handleViewReceiptFromModal}
        onTransactAgain={handleTransactAgain}
      />
    </>
  );
};

export default AtmMachine;
